import React from 'react';
import { motion } from 'framer-motion';
import { Linkedin, Quote, UserCircle, ArrowRight, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const TeamMember: React.FC<{
  name: string;
  role: string;
  description: string;
  linkedin?: string;
  orcid?: string;
  internalLink?: string;
  imageSrc: string;
}> = ({ name, role, description, linkedin, orcid, internalLink, imageSrc }) => (
  <div className="glass-card p-10 rounded-[3rem] transition-theme flex flex-col md:flex-row gap-10 items-start">
    <div className="w-full md:w-64 aspect-square rounded-[2.5rem] bg-[var(--accent-blue-soft)] flex-shrink-0 overflow-hidden flex items-center justify-center text-[var(--accent-blue)] transition-theme shadow-inner border border-[var(--card-border)] relative">
      <img 
        src={imageSrc} 
        alt={name} 
        className="w-full h-full object-cover hover:grayscale-0 transition-all duration-500" 
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
      />
      <div className="absolute inset-0 flex items-center justify-center text-[var(--text-muted)] text-xs italic text-center p-4">
         
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
    </div>
    <div className="flex-grow">
      <h3 className="text-2xl font-bold text-[var(--text-main)] mb-1 transition-theme">{name}</h3>
      <p className="text-[var(--accent-blue)] font-semibold mb-6 transition-theme">{role}</p>
      <p className="text-[var(--text-muted)] mb-8 leading-relaxed italic text-sm transition-theme">{description}</p>
      <div className="flex gap-6">
        {linkedin && (
          <a href={linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-bold text-[var(--text-main)] hover:text-[var(--accent-blue)] transition-all">
            <Linkedin className="w-5 h-5" /> LinkedIn
          </a>
        )}
        {orcid && (
          <a href={orcid} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm font-bold text-[var(--text-main)] hover:text-[var(--accent-blue)] transition-all">
            <ExternalLink className="w-5 h-5" /> ORCID
          </a>
        )}
        {internalLink && (
          <Link to={internalLink} className="flex items-center gap-2 text-sm font-bold text-[var(--accent-blue)] hover:brightness-90 transition-all group">
            <UserCircle className="w-5 h-5" /> View Profile <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}
      </div>
    </div>
  </div>
);

const Team: React.FC = () => {
  return (
    <div className="container mx-auto px-6 max-w-6xl space-y-32 pt-32">
      <div className="text-center">
        <h1 className="text-5xl font-bold mb-6 text-[var(--text-main)] transition-theme">Our Team</h1>
        <p className="text-[var(--text-muted)] max-w-2xl mx-auto transition-theme">
          PreciQube is led by a founding team combining deep technical expertise with execution-focused leadership to build defensible hardware for real-world deployment.
        </p>
      </div>

      <div className="space-y-12">
        <TeamMember 
          name="Prakhar Maheshwari"
          role="Co-Founder & CEO"
          imageSrc="/images/prakhar.png"
          description="Experienced in precision optics and quantum-enabled systems, with a background in building scalable hardware and leading multidisciplinary teams. Prakhar drives PreciQube's vision, product direction, and execution, bridging deep physics, engineering trade-offs, and long-term system design."
          linkedin="https://linkedin.com/in/prakhar-maheshwari-b0b797204"
        />

        <TeamMember
          name="Prof. A. R. Ganesan"
          role="Co-Founder & Technical Advisor"
          imageSrc="/images/ganesan.png"
          description="Senior Professor at IIT Madras with over 35 years of global experience in applied optics, adaptive optics, and optical instrumentation. Known for pioneering indigenous wavefront sensors and laser beam steering systems in India, and author of the revised editions of Hecht’s Optics. He guides PreciQube's technical rigour."
          internalLink="/professor"
        />

        <TeamMember
          name="Pratik Chakraborty"
          role="Tech Advisor & Co-Builder"
          imageSrc="/images/Pratik-Chakraborty.jpg"
          description="A quantum optics researcher specialising in cavity optomechanics, with work spanning micromechanical oscillators, metamaterials and membranes, and cryogenic systems aimed at reaching the quantum back-action noise floor. He holds an M.Tech. in Applied Optics from IIT Delhi and the dissertation work for the graduate program was done at VLBAI (Very Long Baseline Atom Interferometry), Institute of Quantum Optics at Leibniz Universität Hannover, on the topic “Active Laser pointing stabilisation for long baseline Atom Interferometry”, under the supervision of Dr. Dennis Schlippert."
          orcid="https://orcid.org/0000-0002-0994-7394"
        />
      </div>

      <section className="bg-[var(--footer-bg)] rounded-[4rem] p-12 md:p-20 text-white relative overflow-hidden transition-theme">
        <Quote className="absolute top-10 right-10 w-40 h-40 text-blue-500/10" />
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-3xl font-bold mb-12 flex items-center gap-4">
            <span className="w-12 h-0.5 bg-[var(--accent-blue)]"></span>
            Founder's Note
          </h2>
          <div className="space-y-6 text-white/70 text-lg leading-relaxed italic">
            <p>
              "PreciQube began with a simple yet challenging question: why do many powerful optical and quantum 
              techniques remain confined to laboratories, when their impact could be transformative in 
              real-world healthcare systems?
            </p>
            <p>
              Building hardware at this intersection is a complex process. It demands patience, rigorous 
              engineering, and respect for the underlying physics. Our approach is to carefully make the foundations, 
              encompassing optical stability, reproducibility, and defensible IP, before advancing toward large-scale applications.
            </p>
            <p>
              We are early, deliberate, and collaborative by design. Every conversation with clinicians, 
              researchers, and industry partners helps shape the systems we are building."
            </p>
          </div>
          <div className="mt-12 pt-12 border-t border-white/10">
            <p className="font-bold text-xl text-white">Prakhar Maheshwari</p>
            <p className="text-[var(--accent-blue)]">Co-Founder & CEO, PreciQube</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Team;
